import { useCallback, useMemo, useState, MouseEvent, useEffect } from "react";
import {
  InputCell,
  useGridColumnDrag,
  useDataGridRowSelection,
} from "@bst/react-ui";
import {
  Column,
  CalculatedColumn,
  SortColumn,
} from "@bst/react-data-grid";
import {
  InvitationStrategy,
  UserDTO,
  UserExistingRolesResponseDTO,
} from "../../../apiclients/bstinsights";
import { useStores } from '../../../stores/useStores';
import { HeaderCell } from "../Views/HeaderCell";
import { useGetColumns } from "./useGetColumns";
import { useCellRender } from './useCellRender';
import { useUpdateUserRole } from "./useUpdateUserRole";
import { useGridPreferences } from "./useGridPreferences";
import { useGetInvitableUsers } from './useGetInvitableUsers';
import {
  COLUMN_WIDTH,
  INDETERMINATE,
  SENDING_INVITATION_STATUS,
  SENDING_INVITATION_FAILED,
} from './utils/constants';
import { FROZEN_COLUMN, EDITABLE_CELL, FILTER_TYPE } from '../../../stores/UserManagement/constants';
import { useGetExistingRoles } from './useGetExistingRoles';

export const useColumns = (data: UserDTO[] = []) => {
  const gridRef = useGridColumnDrag(COLUMN_WIDTH)
  const cellRender = useCellRender()
  const allColumns = useGetColumns()
  const [anchorPosition, setAnchorPosition] = useState({ top: 0, left: 0 });
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [columnSelection, setColumnSelection] = useState(allColumns.filter((c) => c.visible).map((c) => c.key))
  const [columnMenuActiveCell, setColumnMenuActiveCell] = useState("");
  const [invitationStrategy, setInvitationStrategy] = useState<InvitationStrategy>(0);
  const [existingRolesCount, setExistingRolesCount] = useState<UserExistingRolesResponseDTO>()
  const { userManagementStore } = useStores()

  const { data: invitableUsers = [] } = useGetInvitableUsers();
  const { data: existingRoles, isFetched: rolesIsFetched } = useGetExistingRoles({ filters: userManagementStore.getGridFilters() })
  const {
    sort,
    isFetched,
    preferences,
    updateGridPreferences,
    debouncedSaveGridPreferences,
  } = useGridPreferences();
  const { addUserRole, removeUserRole } = useUpdateUserRole();
  const { selected, handleSelectRows, handleClearSelectedRows } = useDataGridRowSelection();

  useEffect(() => {
    if (rolesIsFetched && existingRoles) {
      setExistingRolesCount(existingRoles)
    }
  }, [existingRoles, rolesIsFetched])

  useEffect(() => {
    if (preferences && isFetched) {
      const data = preferences.columnSelection || allColumns.filter((c) => c.visible).map((c) => c.key)
      setColumnSelection(data)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched]);

  const reorderColumns = (sourceKey: string, targetKey: string) => {
    const sourceIndex = columnSelection.indexOf(sourceKey);
    const targetIndex = columnSelection.indexOf(targetKey);
    const newSelection = [...columnSelection].filter((key) => key !== sourceKey)
    const direction = sourceIndex < targetIndex ? 'right' : 'left';
    // Recalculate target index if source was before
    const adjustedTargetIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
    if (direction === 'right') {
      // insert after target or at the end
      newSelection.splice(adjustedTargetIndex + 1, 0, sourceKey)
    } else {
      // insert before target
      newSelection.splice(adjustedTargetIndex, 0, sourceKey)
    }
    setColumnSelection(newSelection)
    return newSelection
  }

  const onColumnsReorder = (sourceKey: string, targetKey: string) => {
    if (sourceKey === FROZEN_COLUMN) {
      return
    }
    const newColumnsOrder = reorderColumns(sourceKey, targetKey);

    debouncedSaveGridPreferences({
      ...preferences,
      columns: preferences?.columns || {},
      columnSelection: newColumnsOrder,
    });
  };

  const handleColumnShow = (value: boolean) => {
    setShowColumnMenu(value);
    setColumnMenuActiveCell("");
  };

  const handleColumnVisibility = (column: { id: string; visible: boolean, columnKey: string }) => {
    let newSelection = [...columnSelection]
    if (column.visible) {
      if (columnMenuActiveCell) {
        const targetIndex = newSelection.findIndex((key) => key === columnMenuActiveCell)
        newSelection.splice(targetIndex + 1, 0, column.id)
      }
    } else {
      newSelection = newSelection.filter((key) => key !== column.id)
      // Remove filter when hiding column
      userManagementStore.removeFilter(column.columnKey)
      userManagementStore.removeUncheckedColumn(column.columnKey)
    }
    setColumnSelection(newSelection)

    debouncedSaveGridPreferences({
      ...preferences,
      columns: preferences?.columns || {},
      columnSelection: newSelection,
    });
  }

  const toggleColumnMenu = useCallback(
    (column: CalculatedColumn<UserDTO>, event?: MouseEvent) => {
      if (event) {
        const target = event.target as HTMLElement;
        const rect = target.getBoundingClientRect();
        setAnchorPosition({
          top: rect.top + window.scrollY,
          left: rect.left + window.scrollX,
        });
      }
      if (columnMenuActiveCell === column.key) {
        setShowColumnMenu(!showColumnMenu);
        setColumnMenuActiveCell(!showColumnMenu ? column.key : "");
      } else {
        setShowColumnMenu(true);
        setColumnMenuActiveCell(column.key);
      }
    },
    [columnMenuActiveCell, showColumnMenu]
  );

  const handleRoleChange = useCallback(
    (roleId: number, user: UserDTO, isChecked: boolean) => {
      let groups = [...(user.groups ?? [])]
      if (isChecked) {
        addUserRole({ roleId, user });
        groups.push(roleId)
      } else {
        removeUserRole({ roleId, user });
        groups = groups.filter((id) => id !== roleId);
      }

      if (groups.length === 0 && selected.includes(user.userId as string)) {
        handleSelectRows([user.userId as string]);
      }
    },
    [addUserRole, handleSelectRows, removeUserRole, selected]
  );

  const handleSortChange = useCallback(
    (columnKey: string) => {
      let nextSort: SortColumn | undefined;

      if (sort?.columnKey !== columnKey) {
        nextSort = { columnKey, direction: "ASC" };
      } else if (sort.direction === "ASC") {
        nextSort = { columnKey, direction: "DESC" };
      } else {
        nextSort = undefined;
      }

      if (nextSort) {
        updateGridPreferences({
          ...(preferences ?? { columns: {} }),
          sort: {
            column: nextSort.columnKey as keyof UserDTO,
            direction: nextSort.direction,
          },
        });
      } else {
        updateGridPreferences({
          ...(preferences ?? { columns: {} }),
          sort: undefined,
        });
      }
    },
    [sort, preferences, updateGridPreferences]
  );

  // istanbul ignore next
  const getCheckedState = useCallback(() => {
    if (invitationStrategy === 0) {
      if (selected.length === 0) {
        return false;
      } else if (
        data.every((row) =>
          selected.find((id) => typeof row === "object" && row.userId === id)
        )
      ) {
        return true;
      } else {
        return INDETERMINATE;
      }
    } else {
      return selected.length > 0 ? INDETERMINATE : true;
    }
  }, [data, selected, invitationStrategy]);

  const handleColumnResizeEnd = (index: number, width: number) => {
    const columnKey = columnSelection[index];

    const columnPreferences =
      preferences?.columns?.[columnKey as keyof typeof preferences.columns];
    debouncedSaveGridPreferences({
      ...preferences,
      columns: {
        ...preferences?.columns,
        [columnKey]: {
          ...columnPreferences,
          width,
        },
      },
    });
  };

  const isRowDisabled = (row: UserDTO) => (
    row.accountStatusName === SENDING_INVITATION_STATUS ||
    row.accountStatusName === SENDING_INVITATION_FAILED
  )

  const getRowClass = (row: UserDTO) => {
    if (isRowDisabled(row)) {
      return 'disabled-row'
    }
  }

  const handleCheckedChange = useCallback(() => {
    setInvitationStrategy((previousStrategy) => {
      if (getCheckedState() === INDETERMINATE) {
        handleClearSelectedRows();
        return 0;
      } else {
        return Number(!previousStrategy) as 0 | 1;
      }
    });
  }, [getCheckedState, handleClearSelectedRows])

  // istanbul ignore next
  const tableColumns: Column<UserDTO>[] = useMemo(
    () => {
      const columns = columnSelection.map((key) => allColumns.find((c) => c.key === key)) as typeof allColumns
      return columns
        .map((column) => {
          const width = preferences?.columns?.[column.key as keyof typeof preferences.columns]?.width || COLUMN_WIDTH
          return {
            ...column,
            width,
            draggable: !column.frozen,
            minWidth: COLUMN_WIDTH,
            renderCell: (props) => cellRender({
              ...props,
              columnKey: column.columnKey,
              selected,
              disabled: isRowDisabled(props.row),
              invitationStrategy,
              handleSelectRows,
              handleRoleChange
            }),
            editable: (row) => EDITABLE_CELL.includes(column.key) && !isRowDisabled(row),
            renderEditCell: InputCell,
            renderHeaderCell: (props) => (
              <HeaderCell
                {...props}
                columnKey={column.columnKey}
                sortDirection={
                  sort?.columnKey === column.columnKey
                    ? sort?.direction
                    : undefined
                }
                filterType={FILTER_TYPE[column.columnKey]}
                checked={getCheckedState()}
                onToggleSort={() => handleSortChange(column.columnKey)}
                toggleColumnMenu={toggleColumnMenu}
                columnMenuActive={columnMenuActiveCell === column.key}
                handleCheckedChange={handleCheckedChange}
                existingRolesCount={existingRolesCount}
              />
            ),
          };
        })
    },
    [allColumns, cellRender, columnMenuActiveCell, columnSelection, existingRolesCount, getCheckedState, handleCheckedChange, handleRoleChange, handleSelectRows, handleSortChange, invitationStrategy, preferences, selected, sort?.columnKey, sort?.direction, toggleColumnMenu]
  );

  const comboboxItems = useMemo(
    () =>
      allColumns
        .filter((item) => !item.frozen)
        .map((item) => ({
          id: item.key,
          title: item.name,
          visible: columnSelection.includes(item.key),
          columnKey: item.columnKey
        })),
    [columnSelection, allColumns]
  );

  const selectedCount = useMemo(() => (
    invitationStrategy === 0
      ? selected.length
      : invitableUsers.length > 0
        ? invitableUsers.length - selected.length
        : 0
  ), [invitableUsers.length, invitationStrategy, selected.length])

  return {
    sort,
    gridRef,
    selected,
    columns: tableColumns,
    comboboxItems,
    showColumnMenu,
    anchorPosition,
    invitationStrategy,
    selectedCount,
    getRowClass,
    onColumnsReorder,
    setShowColumnMenu: handleColumnShow,
    handleColumnResizeEnd,
    handleColumnVisibility,
    handleClearSelectedRows,
  };
};
